import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Home, Layers, Tags, Boxes, SlidersHorizontal, Menu, X, Sun, Moon, LayoutDashboard, HelpCircle, LogOut, Crown, Shield, User } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { recordVisit } from "@/lib/navTracker";
import cdiLogo from "@/assets/cdi-logo.png";
import CoachMarks from "@/components/CoachMarks";
import { loadMigrationPlan, runMigration, MIGRATION_SESSION_KEY } from "@/lib/templateMigration";
import { toast } from "sonner";
import TrialBanner from "@/components/auth/TrialBanner";
import TrialExpiredLockout from "@/components/auth/TrialExpiredLockout";
import ImpersonationBanner from "@/components/admin/ImpersonationBanner";
import { useAuth, useTier } from "@/context/AuthContext";

const navItems = [
  { to: "/home", label: "Home", end: true, testid: "nav-home", Icon: Home },
  { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard", Icon: LayoutDashboard },
  { to: "/collections", label: "Collections", testid: "nav-collections", Icon: Layers },
  { to: "/categories", label: "Categories", testid: "nav-categories", Icon: Tags },
  { to: "/items", label: "Items", testid: "nav-items", Icon: Boxes },
  { to: "/settings", label: "Settings", testid: "nav-settings", Icon: SlidersHorizontal },
];

function ThemeToggle() {
  const { resolved, toggle } = useTheme();
  const isDark = resolved === "dark";
  return (
    <button
      data-testid="theme-toggle"
      onClick={toggle}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--border))]"
    >
      {isDark ? <Sun size={16} /> : <Moon size={16} />}
    </button>
  );
}

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { isAdmin } = useTier();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    navigate("/auth/login");
  };

  // Build the visible nav items — insert an Admin link immediately
  // before Settings when the session says is_admin=true. Settings must
  // always stay last so the ordering is stable for non-admin users.
  const visibleNavItems = isAdmin
    ? [
        ...navItems.slice(0, -1),
        { to: "/admin", label: "Admin", testid: "nav-admin", Icon: Shield },
        navItems[navItems.length - 1],
      ]
    : navItems;

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  // Track every SPA route transition so context-aware Back buttons across
  // the app (Item Detail, Collection, Category Builder, Dashboard) can
  // resolve the previous screen without each calling link having to pass
  // `state={{ from }}`. See lib/navTracker.js for the singleton.
  useEffect(() => {
    recordVisit(location.pathname + location.search);
  }, [location.pathname, location.search]);
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Cold-launch template migration. Runs at most once per browser
  // session (gated by `sessionStorage[MIGRATION_SESSION_KEY]`). Fetches
  // items + category fields, computes the plan silently, and PATCHes
  // any items whose `item_data` has drifted from the current template.
  // The toast only fires when at least one item was actually aligned —
  // clean apps stay silent. All errors are swallowed so a slow or
  // offline network never blocks the app shell from rendering.
  //
  // NB: no `cancelled` guard — React 18 strict-mode fires this effect
  // twice on mount, and any cleanup-based cancellation would abort the
  // legitimate first invocation. The sessionStorage flag is synchronous
  // so the second invocation short-circuits before it starts work.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.sessionStorage.getItem(MIGRATION_SESSION_KEY)) return;
    window.sessionStorage.setItem(MIGRATION_SESSION_KEY, "1");
    (async () => {
      try {
        const plan = await loadMigrationPlan();
        if (plan.total === 0) return;
        const { migrated } = await runMigration(plan);
        if (migrated.length > 0) {
          toast.success("Templates updated.");
        }
      } catch { /* offline or slow — retry next cold launch */ }
    })();
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 bg-[hsl(var(--bg-elevated))] border-b border-[hsl(var(--border))] backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--bg-elevated))]/85">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-[13px] shrink-0" data-testid="brand-logo">
            <img
              src={cdiLogo}
              alt="CDI"
              data-testid="brand-logo-img"
              className="block shrink-0"
            />
            <span className="text-base sm:text-lg font-bold tracking-tight text-[hsl(var(--gold))]">
              Collectables
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {visibleNavItems.map(({ to, label, end, testid, Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={testid}
                className={({ isActive }) => `vp-nav-link ${isActive ? "is-active" : ""}`}
              >
                <Icon size={15} strokeWidth={2} />
                <span>{label}</span>
              </NavLink>
            ))}
            <details className="vp-nav-about relative" data-testid="nav-about-dropdown">
              <summary className="vp-nav-link cursor-pointer list-none" data-testid="nav-about">
                <HelpCircle size={15} strokeWidth={2} />
                <span>About</span>
              </summary>
              <div
                className="absolute right-0 top-full mt-1 w-56 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-lg shadow-vp-pop z-50 py-1"
                role="menu"
              >
                <NavLink to="/about"               data-testid="nav-about-app"          className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">About CDI Collectables</NavLink>
                <NavLink to="/faq"                 data-testid="nav-about-faq"          className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">FAQ</NavLink>
              </div>
            </details>
            {user?.basic_owned && !user?.premium_plan && (
              <NavLink
                to="/auth/purchase-plan"
                data-testid="nav-upgrade-premium"
                className={({ isActive }) => `vp-nav-link ${isActive ? "is-active" : ""}`}
              >
                <Crown size={15} strokeWidth={2} />
                <span>Upgrade to Premium</span>
              </NavLink>
            )}
            {user?.tier === "premium" && (
              <NavLink
                to="/account/subscription"
                data-testid="nav-manage-subscription"
                className={({ isActive }) => `vp-nav-link ${isActive ? "is-active" : ""}`}
              >
                <Crown size={15} strokeWidth={2} />
                <span>Manage Subscription</span>
              </NavLink>
            )}
            <details className="vp-nav-about relative" data-testid="nav-account-dropdown">
              <summary className="vp-nav-link cursor-pointer list-none" data-testid="nav-account">
                <User size={15} strokeWidth={2} />
                <span>Account</span>
              </summary>
              <div
                className="absolute right-0 top-full mt-1 w-56 bg-[hsl(var(--bg-elevated))] border border-[hsl(var(--border))] rounded-lg shadow-vp-pop z-50 py-1"
                role="menu"
              >
                <NavLink to="/account"                 data-testid="nav-account-my"           className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">My Account</NavLink>
                <NavLink to="/account/plan-pricing"    data-testid="nav-account-plan"         className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">Plan &amp; Pricing</NavLink>
                <NavLink to="/account/change-password" data-testid="nav-account-password"     className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">Change Password</NavLink>
                <NavLink to="/legal/subscription"      data-testid="nav-account-subscription" className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">Subscription Terms</NavLink>
                <NavLink to="/legal/privacy"           data-testid="nav-account-privacy"      className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">Privacy Policy</NavLink>
                <NavLink to="/legal/refund"            data-testid="nav-account-refund"       className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">Refund Policy</NavLink>
                <NavLink to="/legal/contact"           data-testid="nav-account-contact"      className="block px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]">Contact</NavLink>
                <button
                  type="button"
                  onClick={handleLogout}
                  data-testid="nav-account-logout"
                  className="block w-full text-left px-4 py-2 text-sm hover:bg-[hsl(var(--muted))]"
                >Logout</button>
              </div>
            </details>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0 border border-[hsl(var(--border))]"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 vp-fade-bg lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <aside
            data-testid="mobile-drawer"
            className="fixed top-0 left-0 bottom-0 z-50 w-72 max-w-[85%] bg-[hsl(var(--bg-elevated))] border-r border-[hsl(var(--border))] shadow-vp-pop vp-slide-in-left lg:hidden flex flex-col"
            role="dialog"
            aria-modal="true"
          >
            <div className="h-16 px-5 flex items-center justify-between border-b border-[hsl(var(--border))]">
              <div className="flex items-center gap-[13px]">
                <img
                  src={cdiLogo}
                  alt="CDI"
                  data-testid="mobile-brand-logo-img"
                  className="block shrink-0"
                />
                <span className="text-base font-bold tracking-tight">
                  Collectables
                </span>
              </div>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMobileOpen(false)}
                className="vp-btn vp-btn-ghost vp-btn-sm !h-9 !w-9 !p-0"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto p-4 flex flex-col gap-1" aria-label="Mobile">
              {visibleNavItems.map(({ to, label, end, testid, Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  data-testid={`m-${testid}`}
                  className={({ isActive }) => `vp-nav-link !py-3 ${isActive ? "is-active" : ""}`}
                >
                  <Icon size={16} />
                  <span>{label}</span>
                </NavLink>
              ))}
              <details className="mt-2 pt-2 border-t border-[hsl(var(--border))]" data-testid="m-nav-about-group">
                <summary className="cursor-pointer list-none">
                  <div className="px-3 py-3" data-testid="m-nav-about-header">About</div>
                </summary>
                <NavLink to="/about"              onClick={() => setMobileOpen(false)} data-testid="m-nav-about-app"          className="vp-nav-link !py-3"><HelpCircle size={16} /><span>About CDI Collectables</span></NavLink>
                <NavLink to="/faq"                onClick={() => setMobileOpen(false)} data-testid="m-nav-about-faq"          className="vp-nav-link !py-3"><HelpCircle size={16} /><span>FAQ</span></NavLink>
              </details>
              <details className="mt-2 pt-2 border-t border-[hsl(var(--border))]" data-testid="m-nav-account-group">
                <summary className="cursor-pointer list-none">
                  <div className="px-3 py-3" data-testid="m-nav-account-header">Account</div>
                </summary>
                <NavLink to="/account"                 onClick={() => setMobileOpen(false)} data-testid="m-nav-account-my"           className="vp-nav-link !py-3"><User size={16} /><span>My Account</span></NavLink>
                <NavLink to="/account/plan-pricing"    onClick={() => setMobileOpen(false)} data-testid="m-nav-account-plan"         className="vp-nav-link !py-3"><User size={16} /><span>Plan &amp; Pricing</span></NavLink>
                <NavLink to="/account/change-password" onClick={() => setMobileOpen(false)} data-testid="m-nav-account-password"     className="vp-nav-link !py-3"><User size={16} /><span>Change Password</span></NavLink>
                <NavLink to="/legal/subscription"      onClick={() => setMobileOpen(false)} data-testid="m-nav-account-subscription" className="vp-nav-link !py-3"><User size={16} /><span>Subscription Terms</span></NavLink>
                <NavLink to="/legal/privacy"           onClick={() => setMobileOpen(false)} data-testid="m-nav-account-privacy"      className="vp-nav-link !py-3"><User size={16} /><span>Privacy Policy</span></NavLink>
                <NavLink to="/legal/refund"            onClick={() => setMobileOpen(false)} data-testid="m-nav-account-refund"       className="vp-nav-link !py-3"><User size={16} /><span>Refund Policy</span></NavLink>
                <NavLink to="/legal/contact"           onClick={() => setMobileOpen(false)} data-testid="m-nav-account-contact"      className="vp-nav-link !py-3"><User size={16} /><span>Contact</span></NavLink>
                <button
                  type="button"
                  onClick={() => { handleLogout(); setMobileOpen(false); }}
                  data-testid="m-nav-account-logout"
                  className="vp-nav-link !py-3 w-full text-left"
                >
                  <LogOut size={16} />
                  <span>Logout</span>
                </button>
              </details>
            </nav>
            <div className="p-4 border-t border-[hsl(var(--border))]">
              <PlanFooterLink onClick={() => setMobileOpen(false)} />
              <div className="text-xs vp-muted">CDI Collectables · v2</div>
            </div>
          </aside>
        </>
      )}

      {/* `overflow-x-hidden` here contains any unintended horizontal
          overflow from a single page (long names, absolute-positioned
          icon clusters, filter bars that don't wrap on very narrow
          viewports, etc.) so the user never sees a horizontal scroll
          bar on mobile. Pages that legitimately need a horizontal
          scroll (e.g. the Item Detail gallery strip) opt-in locally
          via `overflow-x-auto` on their own inner container, which is
          unaffected by this clip. */}
      <main className="flex-1 vp-fade-in overflow-x-hidden">
        <ImpersonationBanner />
        <TrialBanner />
        <UpgradeNowCard />
        <Outlet />
      </main>

      <footer className="border-t border-[hsl(var(--border))] bg-[hsl(var(--bg-elevated))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <span className="vp-label">© 2026 CDI Collectables | <Link to="/legal/terms" data-testid="footer-terms">Terms</Link> | <Link to="/legal/privacy" data-testid="footer-privacy">Privacy</Link> | <Link to="/legal/refund" data-testid="footer-refund">Refund</Link> | <Link to="/legal/subscription" data-testid="footer-subscription">Subscription Terms</Link> | <Link to="/contact" data-testid="footer-contact">Contact</Link></span>
        </div>
      </footer>

      <CoachMarks />
      <TrialExpiredLockout />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 5 pricing — inline helper components.
// Kept in Layout.jsx (rather than a new component file) so this task
// touches only the files listed in the approved plan.
// ---------------------------------------------------------------------------

// UpgradeNowCard — visible only for `plan === "trial"` users. Sits under
// the thin TrialBanner and above <Outlet />. Bronze "Buy Basic" CTA.
// Premium options are shown as disabled rows with the standard helper
// text. Purely informational — real selection happens on /auth/purchase-plan.
// Hidden for admin sessions or any `/admin` route so the dashboard stays
// promo-free.
function UpgradeNowCard() {
  const { user } = useAuth();
  const { isAdmin } = useTier();
  const { pathname } = useLocation();
  if (isAdmin || pathname.startsWith("/admin")) return null;
  // Belt-and-braces: hide once Basic is owned even if `plan` hasn't yet
  // transitioned off "trial" (e.g. stale session before /refresh lands).
  if (!user || user.plan !== "trial" || user.basic_owned) return null;
  return (
    <div
      data-testid="upgrade-now-card"
      className="max-w-7xl mx-auto px-4 sm:px-6 mt-4"
    >
      <div
        className="rounded-lg p-5"
        style={{
          background: "linear-gradient(180deg, #1a1a1a 0%, #14100a 100%)",
          border: "1px solid #C9A86A",
        }}
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div
              className="text-base font-semibold mb-1"
              style={{ color: "#f7f7f7", letterSpacing: "0.3px" }}
            >Upgrade Now</div>
            <div className="text-xs" style={{ color: "#9a9a9a" }}>
              Basic unlocks the full app. Add Premium later for extra features.
            </div>
          </div>
          <Link
            to="/auth/purchase-plan"
            data-testid="upgrade-now-buy-basic"
            className="inline-flex items-center px-4 py-2 rounded-md text-xs font-bold"
            style={{ background: "#C9A86A", color: "#0f0f0f", letterSpacing: "0.5px" }}
          >Buy Basic →</Link>
        </div>
      </div>
    </div>
  );
}

// PlanFooterLink — bronze link surfaced in the mobile drawer footer.
//   • Trial users:               no link (banner + card handle upsell)
//   • Basic (no premium):        no link (Premium is Coming Soon — Phase 7 TBAC)
//   • Premium subscribers:       "Manage Subscription" → /account/subscription
function PlanFooterLink({ onClick }) {
  const { user } = useAuth();
  if (!user || user.plan === "trial") return null;
  if (!user.basic_owned) return null;

  const isPremium = !!user.premium_plan;
  if (!isPremium) return null; // Phase 7: no active Premium CTA
  return (
    <Link
      to="/account/subscription"
      onClick={onClick}
      data-testid="drawer-manage-subscription"
      className="block mb-3 px-3 py-2 rounded-md text-sm font-semibold text-center"
      style={{
        background: "transparent",
        color: "#C9A86A",
        border: "1px solid #C9A86A",
        letterSpacing: "0.3px",
      }}
    >Manage Subscription</Link>
  );
}
